  const formatEditableUntil = (createdAt: string): string => {
    const created = new Date(createdAt);
    const editableUntil = new Date(created.getTime() + 120 * 60 * 1000);
    return editableUntil.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
  };
import React, { useState, useCallback, useEffect } from 'react';
import { Card } from '@/components/ui/Card';
import { SubtitleText } from '@/components/ui/SubtitleText';
import { BodyText } from '@/components/ui/BodyText';
import { Button } from '@/components/ui/Button';
import { usePatientNotes } from '@/hooks/usePatientNotes';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/utils/cn';
import { AddPatientNoteEditor } from './AddPatientNoteEditor';
import {
  MAX_PREVIEW_CHARS,
  MAX_PREVIEW_LINES,
  PatientNoteListItemContentCard,
  PREVIEW_MAX_HEIGHT_PX,
  truncateMarkdown,
} from './PatientNoteListItemContentCard';

interface PatientNotesHistoryCardProps {
  dietitianId: string;
  patientId: string;
  pageSize?: number;
  title?: string;
}

export const PatientNotesHistoryCard: React.FC<PatientNotesHistoryCardProps> = ({
  dietitianId,
  patientId,
  pageSize = 10,
  title = 'Patient Notes',
}) => {
  const { isDark } = useTheme();
  const {
    items,
    loading,
    error,
    page,
    pageSize: currentPageSize,
    totalItems,
    goToPage,
    refresh,
    updateNote,
  } = usePatientNotes({ dietitianId, patientId, pageSize });
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState<string>('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [noteHeights, setNoteHeights] = useState<Record<string, number>>({});

  // Always start unexpanded when the item list changes (e.g., pagination or refresh)
  useEffect(() => {
    setExpandedId(null);
  }, [items]);

  const totalPages = Math.max(1, Math.ceil(totalItems / currentPageSize));

  const handleHeightChange = useCallback((id: string, height: number) => {
    setNoteHeights((prev) => {
      if (prev[id] === height) return prev;
      return { ...prev, [id]: height };
    });
  }, []);

  const toggleExpand = useCallback((id: string) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setExpandedId((prev) => (prev === id ? null : id));
    setTimeout(() => setIsAnimating(false), 300);
  }, [isAnimating]);

  const startEditing = (id: string, content: string) => {
    setEditingId(id);
    setEditingContent(content);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingContent('');
  };

  const saveEdit = async (noteId: string) => {
    if (!editingContent.trim()) return;
    await updateNote(noteId, editingContent);
    setEditingId(null);
    setEditingContent('');
  };

  const isWithinEditWindow = (createdAt: string): boolean => {
    const created = new Date(createdAt).getTime();
    const now = Date.now();
    const diffMinutes = (now - created) / (1000 * 60);
    return diffMinutes <= 120;
  };

  const handlePrev = async () => {
    if (page > 1) {
      await goToPage(page - 1);
    }
  };

  const handleNext = async () => {
    if (page < totalPages) {
      await goToPage(page + 1);
    }
  };

  return (
    <Card
      className={cn(
        'p-6 transition-shadow duration-200',
      )}
    >
      <div className="flex items-center justify-between mb-4">
        <SubtitleText className="font-semibold">{title}</SubtitleText>
        <div className="flex items-center gap-2">
          <AddPatientNoteEditor dietitianId={dietitianId} patientId={patientId} />
          <Button variant="outline" size="sm" onClick={() => refresh()} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-16 rounded animate-pulse"
              style={{
                background: isDark
                  ? 'linear-gradient(90deg, rgba(64,64,64,0.6), rgba(88,88,88,0.6), rgba(64,64,64,0.6))'
                  : 'linear-gradient(90deg, rgba(229,231,235,0.8), rgba(243,244,246,0.8), rgba(229,231,235,0.8))',
              }}
            />
          ))}
        </div>
      ) : error ? (
        <div
          className={cn(
            'text-center p-4 rounded',
            isDark ? 'bg-error-900/25 text-error-200' : 'bg-error-50 text-error-700'
          )}
        >
          <BodyText>{error}</BodyText>
          <Button className="mt-3" variant="primary" size="sm" onClick={() => refresh()}>
            Retry
          </Button>
        </div>
      ) : items.length === 0 ? (
        <div
          className={cn(
            'text-center p-6 rounded',
            isDark ? 'bg-secondary-900/50 text-secondary-200' : 'bg-secondary-50 text-secondary-700'
          )}
        >
          <BodyText variant="muted">No patient notes found.</BodyText>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((note) => {
            const isExpanded = expandedId === note.id;
            const { needsTruncation } = truncateMarkdown(note.contentMarkdown, MAX_PREVIEW_CHARS, MAX_PREVIEW_LINES);
            const measuredHeight = noteHeights[note.id];
            const showToggle = measuredHeight !== undefined
              ? measuredHeight > PREVIEW_MAX_HEIGHT_PX
              : needsTruncation;

            const created = new Date(note.createdAt);
            const createdAtLabel = created.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
            const canEdit = isWithinEditWindow(note.createdAt);
            const isEditing = editingId === note.id;

            return (
              <div
                key={note.id}
                className={cn(
                  'rounded p-3 space-y-2 border transition-shadow duration-200 hover:shadow-lg',
                )}
              >
                <div className="flex justify-end">
                  <BodyText
                    variant="muted"
                    size="sm"
                    aria-label="note created at"
                    className={cn(isDark ? 'text-secondary-300' : 'text-secondary-700')}
                  >
                    {createdAtLabel}
                  </BodyText>
                </div>
                {canEdit && !isEditing && (
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => startEditing(note.id, note.contentMarkdown)}
                    >
                      Edit
                    </Button>
                  </div>
                )}
                {isEditing ? (
                  <div className="space-y-2">
                    <textarea
                      value={editingContent}
                      onChange={(e) => setEditingContent(e.target.value)}
                      className={cn(
                        'w-full rounded border p-2 text-sm',
                        isDark ? 'bg-secondary-900 border-secondary-700 text-secondary-50' : 'bg-white border-secondary-300 text-secondary-900'
                      )}
                      rows={6}
                    />
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={cancelEditing}>
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={!editingContent.trim()}
                        onClick={() => saveEdit(note.id)}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <PatientNoteListItemContentCard
                  content={note.contentMarkdown}
                  isExpanded={isExpanded}
                  isDark={isDark}
                  onHeightChange={(h) => handleHeightChange(note.id, h)}
                />
                )}
                {showToggle && !isEditing && (
                  <div className="flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleExpand(note.id)}
                      aria-expanded={isExpanded}
                    >
                      {isExpanded ? 'Show less' : 'Show more'}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between" aria-label="patient notes pagination">
        <Button variant="outline" size="sm" onClick={() => handlePrev()} disabled={loading || page <= 1} aria-label="Previous page">
          Previous
        </Button>
        <BodyText variant="muted" size="sm" aria-live="polite">
          Page {page} of {totalPages}
        </BodyText>
        <Button variant="outline" size="sm" onClick={() => handleNext()} disabled={loading || page >= totalPages} aria-label="Next page">
          Next
        </Button>
      </div>

      <AddPatientNoteEditor
        dietitianId={dietitianId}
        patientId={patientId}
        mode="edit"
        hideTrigger
        isOpenExternal={editingId !== null}
        initialContent={editingContent}
        onCloseExternal={cancelEditing}
        onSaveExternal={async (content) => {
          if (!editingId) return;
          await updateNote(editingId, content);
          cancelEditing();
        }}
        editableUntilLabel={
          editingId
            ? formatEditableUntil(items.find((n) => n.id === editingId)?.createdAt ?? '')
            : undefined
        }
      />
    </Card>
  );
};

export default PatientNotesHistoryCard;
